import { useState } from "react";
import { useAppStore } from "../store/useAppStore";
import { useNavigate } from "react-router-dom";
import { Home, ArrowRight } from "lucide-react";

export default function Onboarding() {
  const { setOnboardingData } = useAppStore();
  const navigate = useNavigate();

  const [businessName, setBusinessName] = useState("");
  const [focus, setFocus] = useState("Vacant houses");
  const [followUpStyle, setFollowUpStyle] = useState("Friendly");
  const [cadence, setCadence] = useState("7");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await setOnboardingData({
      businessName,
      focus,
      followUpStyle,
      cadence
    }, true);
    navigate("/");
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_top_right,_rgba(16,185,129,0.12),_transparent_30%),linear-gradient(180deg,_#f8fafc_0%,_#eef2ff_100%)] p-4 font-sans">
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(15,23,42,0.03)_0%,transparent_30%,rgba(59,130,246,0.04)_100%)]" />

      <div className="relative z-10 grid w-full max-w-6xl overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-2xl shadow-slate-900/10 lg:grid-cols-[.9fr_1.1fr]">
        <div className="flex flex-col justify-between gap-10 bg-slate-950 px-8 py-10 text-white sm:px-10 lg:px-12">
          <div className="space-y-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-white shadow-sm">
                <Home className="h-6 w-6" />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">LandMiner CRM</p>
                <p className="mt-1 text-sm text-slate-300">Set the tone for how your workspace works.</p>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-emerald-300">Onboarding</p>
              <h1 className="max-w-lg text-4xl font-semibold tracking-tight sm:text-5xl">
                Build a workflow that matches how you already work deals.
              </h1>
              <p className="max-w-xl text-base leading-7 text-slate-300">
                This only takes a minute, but it gives the app a clearer structure so your leads, follow-ups, and outreach feel more intuitive.
              </p>
            </div>
          </div>

          <div className="grid gap-3 text-sm text-slate-300 sm:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">01</p>
              <p className="mt-2 font-medium text-white">Name the business</p>
              <p className="mt-1 leading-6">Personalize the workspace so it feels like your own operating system.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">02</p>
              <p className="mt-2 font-medium text-white">Pick a focus</p>
              <p className="mt-1 leading-6">Tell the app which lead type should get the most attention first.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">03</p>
              <p className="mt-2 font-medium text-white">Set the rhythm</p>
              <p className="mt-1 leading-6">Choose a follow-up cadence that keeps conversations moving.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-center px-6 py-10 sm:px-10 lg:px-12">
          <div className="mb-8 space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">Workspace setup</p>
            <h2 className="text-3xl font-semibold tracking-tight text-slate-950">Set up your CRM</h2>
            <p className="max-w-xl text-sm leading-6 text-slate-500">Let’s get your workspace ready in under 2 minutes.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 rounded-3xl border border-slate-200 bg-slate-50 p-5 sm:p-6">
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase text-slate-500">Business Name</label>
                <input
                  required
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm outline-none transition-colors focus:border-blue-500"
                  placeholder="e.g. Fast Cash Offers"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase text-slate-500">Wholesaling Focus</label>
                <select
                  value={focus}
                  onChange={(e) => setFocus(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm outline-none transition-colors focus:border-blue-500"
                >
                  <option>Vacant houses</option>
                  <option>Tax delinquent</option>
                  <option>Probate</option>
                  <option>Tired landlords</option>
                  <option>Driving for dollars</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase text-slate-500">Default Follow-up Style</label>
                <select
                  value={followUpStyle}
                  onChange={(e) => setFollowUpStyle(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm outline-none transition-colors focus:border-blue-500"
                >
                  <option value="Friendly">Soft and friendly</option>
                  <option value="Professional">Direct and professional</option>
                  <option value="Urgent">Urgent and sales-focused</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase text-slate-500">Default Follow-up Cadence</label>
                <select
                  value={cadence}
                  onChange={(e) => setCadence(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm outline-none transition-colors focus:border-blue-500"
                >
                  <option value="2">2 days</option>
                  <option value="7">7 days</option>
                  <option value="14">14 days</option>
                  <option value="30">30 days</option>
                </select>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
              <p className="text-xs leading-5 text-slate-500">
                You can change these settings later, but this gives the app a clearer starting structure.
              </p>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
              >
                Finish Setup <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
